export const SEGMENTS = ['Enterprise', 'Mid-Market', 'SMB', 'Strategic'] as const

export const STAGES = [
    'New Logo',
    'Upsell',
    'Expansion',
    'Renewal',
    'Pilot',
    'Co-Sell',
    'Land & Expand'
] as const

export type Segment = (typeof SEGMENTS)[number]
export type Stage = (typeof STAGES)[number]
export type Tag = Segment | Stage

export type TagTone =
    | 'blue'
    | 'purple'
    | 'green'
    | 'moss'
    | 'red'
    | 'orange'
    | 'amber'
    | 'teal'
    | 'yellow'
    | 'neutral'

export const TAG_TONES: Record<Tag, TagTone> = {
    'Enterprise': 'blue',
    'Mid-Market': 'moss',
    'SMB': 'yellow',
    'Strategic': 'red',
    'New Logo': 'green',
    'Upsell': 'purple',
    'Expansion': 'green',
    'Renewal': 'green',
    'Pilot': 'orange',
    'Co-Sell': 'amber',
    'Land & Expand': 'teal'
}

export function tagTone(tag: string): TagTone {
    return TAG_TONES[tag as Tag] ?? 'neutral'
}

export type Owner = {
    name: string
    avatar: string
    email: string
    phone: string
    role: string
}

const AVATARS = Array.from(
    { length: 10 },
    (_, index) => `/assets/images/_common/avatars/avatar-${index + 1}.png`
)

const OWNER_NAMES = [
    'Sarah Nguyen',
    'James Taylor',
    'Maria Keller',
    'Nia Jameson',
    'Alex Santos',
    'Mark Darnalds',
    'Drew Nash',
    'Lina Wong',
    'Jamie Fox',
    'Kate Chen',
    'Ricky Brown',
    'Hannah Mills',
    'Emma Green',
    'Oliver Chan',
    'Ava Brooks',
    'Noah Lee',
    'Grace Miller',
    'Chloe Park'
]

export const OWNERS: Owner[] = OWNER_NAMES.map((name, index) => ({
    name,
    avatar: AVATARS[index % AVATARS.length],
    email: `${name.toLowerCase().replace(' ', '.')}@crm.com`,
    phone: `+1 (202) ${String(199 + index).padStart(3, '0')}-${String(5520 + index * 37).slice(-4)}`,
    role: index % 3 === 0 ? "Senior Account Executive" : "Account Executive"
}))

export const CURRENT_USER: Owner = {
    name: 'Jensen Ackles',
    avatar: '/assets/images/_common/avatars/jensen.png',
    email: 'jensen.ackles@crm.com',
    phone: '+1 (202) 184-5501',
    role: "Head of Sales"
}

export function ownerByName(name: string): Owner {
    const owner = OWNERS.find(item => item.name === name)

    if (owner) return owner

    const seed = Array.from(name).reduce((sum, char) => sum + char.charCodeAt(0), 0)
    const handle = name.toLowerCase().trim().replace(/\s+/g, '.')

    return {
        name,
        avatar: AVATARS[seed % AVATARS.length],
        email: handle ? `${handle}@crm.com` : '',
        phone: handle ? `+1 (202) ${String(199 + (seed % 700)).padStart(3, '0')}-${String(5520 + seed * 37).slice(-4)}` : '',
        role: "Account Executive"
    }
}

export function profileByName(name: string): Owner {
    return name === CURRENT_USER.name ? CURRENT_USER : ownerByName(name)
}

export type Company = {
    id: string
    slug: string
    name: string
    logo: string | null
    logoAlt: string | null
    tags: string[]
    owner: string
    openDeals: number
    pipelineValue: number
    winProbability: number
    trend: number[]
    lastInteraction: {
        date: string
        label: string
    }
    activityDays: number
}

export const TREND_PATTERN = [
    false,
    true,
    true,
    false,
    true,
    true,
    false,
    true,
    false,
    true,
    false,
    true,
    true,
    false
]

export const DEFAULT_TREND = [4, 4, 5, 5, 2, 7, 11, 7, 5, 7, 5, 3, 7, 14]

export const SORT_OPTIONS = [
    { value: 'pipelineValue', label: "Pipeline Value" },
    { value: 'winProbability', label: "Win Probability" },
    { value: 'openDeals', label: "Open Deals" },
    { value: 'lastInteraction', label: "Last Interaction" },
    { value: 'name', label: "Company Name" }
] as const

export type SortKey = (typeof SORT_OPTIONS)[number]['value']

export const INTERACTION_TYPES = [
    'Discovery',
    'Demo',
    'Pricing',
    'Security',
    'Legal',
    'Product',
    'Pilot',
    'Exec',
    'QBR Call',
    'Partner',
    'Renewal',
    'Expansion'
] as const

export const ACTIVITY_WINDOWS = [7, 30, 60, 90] as const

export type ActivityWindow = (typeof ACTIVITY_WINDOWS)[number]

export const TREND_WINDOWS = ["Last 7 Days", "Last 30 Days", "Last 90 Days"]

export type ScoreCard = {
    title: string
    description: string
    reviewer: string
    reviewerAvatar: string
    updated: string
    verdict: string
    stars: number
}

export const SCORE_CARDS: ScoreCard[] = [
    {
        title: "Business fit",
        description: "Evaluates how well the company aligns with our ideal customer profile.",
        reviewer: "Emma Green",
        reviewerAvatar: '/assets/images/_common/avatars/detail-2.png',
        updated: "Updated 2h ago",
        verdict: "High potential SN",
        stars: 4
    },
    {
        title: "Technical fit",
        description: "Evaluates technical compatibility, security requirements, and integration readiness.",
        reviewer: "Ricky Brown",
        reviewerAvatar: '/assets/images/_common/avatars/detail-3.png',
        updated: "Updated 2h ago",
        verdict: "High potential SN",
        stars: 4
    },
    {
        title: "Technical fit",
        description: "Evaluates technical compatibility, security requirements, and integration readiness.",
        reviewer: "Taylor Leroy",
        reviewerAvatar: '/assets/images/_common/avatars/detail-1.png',
        updated: "Updated 2h ago",
        verdict: "High potential SN",
        stars: 4
    }
]
